import type { Deal, DealInsert, DealUpdate, DealFlag, DealFlagType, SalesState } from '@/types/deals';
import { normalizeEmail } from '@/lib/contact-vault/normalize';
import { isTrackBDeal } from '@/lib/outreach/nurture-draft';
import { validateTransition, computeDerivedFields } from './deal-workflow';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Mock deals data
const mockDeals: Deal[] = [];

export const mockDealsService = {
  async convertToLocation() {
    throw new Error('Deal location conversion is unavailable in mock mode.');
  },

  async linkToExistingLocation(dealId: string, locationId: string): Promise<Deal> {
    return this.update(dealId, {
      location_id: locationId,
      stage: 'closed_won',
      sales_state: 4,
      nurture_stage: 'exited_won',
    });
  },

  async getAll(): Promise<Deal[]> {
    await delay(300);
    return [...mockDeals];
  },

  async listNurtureTrackEmails(): Promise<Set<string>> {
    const owners = await this.listNurtureTrackOwners();
    return new Set(owners.keys());
  },

  async listNurtureTrackOwners(): Promise<Map<string, { dealId: string; dealName: string; email: string }>> {
    const owners = new Map<string, { dealId: string; dealName: string; email: string }>();
    for (const deal of mockDeals) {
      if (!isTrackBDeal(deal)) continue;
      const email = normalizeEmail(deal.prospect_contact_email);
      if (!email || owners.has(email)) continue;
      owners.set(email, { dealId: deal.id, dealName: deal.name, email });
    }
    return owners;
  },

  async getById(id: string): Promise<Deal | null> {
    await delay(200);
    return mockDeals.find((deal) => deal.id === id) ?? null;
  },

  async getByLocationId(locationId: string): Promise<Deal[]> {
    await delay(200);
    return mockDeals.filter((deal) => deal.location_id === locationId);
  },

  async create(data: DealInsert): Promise<Deal> {
    await delay(400);

    if (data.stage === 'closed_won' && !data.location_id) {
      throw new Error('Cannot create a closed-won prospect without a location. Create the location during deal conversion first.');
    }
    
    // Default sales_state based on stage if not provided
    const stageToState: Record<string, SalesState> = {
      lead: 1,
      qualified: 2,
      proposal_sent: 2,
      verbal_yes: 3,
      closed_won: 4,
      closed_lost: 6,
    };
    
    const newDeal: Deal = {
      id: `deal-${Date.now()}`,
      location_id: data.location_id ?? null,
      contact_id: data.contact_id ?? null,
      name: data.name,
      address: data.address ?? null,
      pos_system: data.pos_system?.trim() || null,
      stage: (data.stage ?? 'lead') as Deal['stage'],
      total_contract_value: data.total_contract_value,
      upfront_amount: data.upfront_amount ?? null,
      recurring_amount: data.recurring_amount ?? null,
      recurring_type: data.recurring_type ?? null,
      estimated_start_date: data.estimated_start_date ?? null,
      source: data.source ?? null,
      owner_id: data.owner_id ?? null,
      notes: data.notes ?? null,
      prospect_contact_name: data.prospect_contact_name ?? null,
      prospect_contact_email: data.prospect_contact_email ?? null,
      prospect_contact_phone: data.prospect_contact_phone ?? null,
      prospect_contact_role: data.prospect_contact_role ?? null,
      // Workflow fields
      mode: data.mode ?? null,
      sales_state: data.sales_state ?? stageToState[data.stage ?? 'lead'] ?? 1,
      flags: data.flags ?? [],
      closed_reason: data.closed_reason ?? null,
      cs_owner: data.cs_owner ?? null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: null,
    };
    mockDeals.push(newDeal);
    return newDeal;
  },

  async update(id: string, data: DealUpdate): Promise<Deal> {
    await delay(400);
    const index = mockDeals.findIndex((deal) => deal.id === id);
    if (index === -1) {
      throw new Error('Deal not found');
    }
    const existing = mockDeals[index];
    if (!existing) {
      throw new Error('Deal not found');
    }

    const wasClosedWon = existing.stage === 'closed_won';
    const willBeClosedWon = data.stage === 'closed_won';

    if (!existing.location_id && willBeClosedWon && !data.location_id) {
      throw new Error('Cannot mark a prospect as closed won without creating or linking a location first.');
    }

    const updated: Deal = {
      ...existing,
      ...data,
      pos_system: data.pos_system !== undefined ? data.pos_system?.trim() || null : existing.pos_system,
      updated_at: new Date().toISOString(),
    };
    mockDeals[index] = updated;

    // Auto-create location order when deal is marked closed_won
    // Only if deal has a location_id (prospects without locations can't create orders)
    if (!wasClosedWon && willBeClosedWon && updated.location_id) {
      try {
        // Dynamically import to avoid circular dependency
        const { mockLocationOrdersService } = await import('./mock-location-orders');

        // Check if order already exists (idempotency)
        const existingOrders = await mockLocationOrdersService.getByDealId(id);
        if (existingOrders.length === 0) {
          // Create order from deal
          await mockLocationOrdersService.createFromDeal({
            id: updated.id,
            location_id: updated.location_id, // TypeScript now knows this is not null
            total_contract_value: updated.total_contract_value,
            name: updated.name,
          });
        }
      } catch (error) {
        // Log error but don't fail the deal update
        console.error('Error creating location order from deal:', error);
      }
    }

    if (existing.location_id !== updated.location_id || existing.stage !== updated.stage) {
      const { refreshAllMockPaidWorkAssociations } = await import('./mock-invoice-paid-work');
      await refreshAllMockPaidWorkAssociations();
    }

    return updated;
  },

  async delete(id: string): Promise<void> {
    await delay(300);
    const index = mockDeals.findIndex((deal) => deal.id === id);
    if (index !== -1) {
      mockDeals.splice(index, 1);
    }
  },

  async deleteClosedLostContact(id: string): Promise<void> {
    const deal = mockDeals.find((item) => item.id === id);
    if (deal && deal.stage !== 'closed_lost') {
      throw new Error('Only Closed Lost contacts can be removed from this list. Opt out or edit the deal instead.');
    }
    await this.delete(id);
  },

  async listPulseEngagement() {
    await delay(0);
    return [];
  },

  async listPulseEngagementScores() {
    await delay(0);
    return [];
  },

  async getPulseEngagementScore() {
    await delay(0);
    return null;
  },

  // ============================================
  // WORKFLOW METHODS
  // ============================================

  // Update sales state with validation (soft guidance + hard gates)
  async updateSalesState(dealId: string, newState: SalesState, exceptionNotes?: string): Promise<Deal> {
    await delay(300);
    const deal = await this.getById(dealId);
    if (!deal) {
      throw new Error('Deal not found');
    }

    // Validate transition (includes hard gate check)
    const validation = validateTransition(deal.sales_state, newState, deal);

    // Hard gate: block State 3→4 transition unless conditions met
    if (validation.hardBlocked) {
      // Check if exception flag exists with notes
      const hasExceptionFlag = deal.flags.some(f => f.type === 'exception' && f.active && f.notes);
      if (!hasExceptionFlag && !exceptionNotes) {
        throw new Error(
          `Cannot transition to State 4: ${validation.reason}. ` +
          `Add Exception Flag with notes to override.`
        );
      }
      // If exception notes provided, add exception flag
      if (exceptionNotes && !hasExceptionFlag) {
        await this.addFlag(dealId, 'exception', exceptionNotes);
        // Re-fetch deal after adding flag
        const updatedDeal = await this.getById(dealId);
        if (!updatedDeal) throw new Error('Deal not found');
        return this.update(dealId, { sales_state: newState });
      }
    }

    // Soft guidance: warn but allow other invalid transitions
    if (!validation.valid && !validation.hardBlocked && validation.warning) {
      console.warn(validation.warning);
      // Still allow the transition (soft enforcement)
    }

    return this.update(dealId, { sales_state: newState });
  },

  // Add flag
  async addFlag(dealId: string, flagType: DealFlagType, notes?: string): Promise<Deal> {
    await delay(200);
    const deal = await this.getById(dealId);
    if (!deal) {
      throw new Error('Deal not found');
    }

    // Exception flag requires notes
    if (flagType === 'exception' && !notes) {
      throw new Error('Exception Flag requires notes to document the override');
    }

    const newFlag: DealFlag = {
      type: flagType,
      active: true,
      created_at: new Date().toISOString(),
      created_by: null,
      notes: notes || null,
      auto_added: false,
    };

    const updatedFlags = [...deal.flags, newFlag];
    return this.update(dealId, { flags: updatedFlags });
  },

  // Remove flag
  async removeFlag(dealId: string, flagType: DealFlagType): Promise<Deal> {
    await delay(200);
    const deal = await this.getById(dealId);
    if (!deal) {
      throw new Error('Deal not found');
    }

    const updatedFlags = deal.flags.map(flag =>
      flag.type === flagType ? { ...flag, active: false } : flag
    );

    return this.update(dealId, { flags: updatedFlags });
  },

  // Check and update flags (called after deal updates)
  async refreshFlags(dealId: string): Promise<Deal> {
    await delay(200);
    const deal = await this.getById(dealId);
    if (!deal) {
      throw new Error('Deal not found');
    }

    // Import services dynamically to avoid circular dependencies
    const { mockInvoicesService } = await import('./mock-invoices');

    let updatedFlags = [...deal.flags];

    // Payment Flag: Check if invoice exists and is unpaid
    const invoices = await mockInvoicesService.getByDealId(dealId);
    const hasUnpaidInvoice = invoices.some(
      inv => inv.status !== 'paid' && inv.status !== 'draft'
    );
    if (hasUnpaidInvoice && !updatedFlags.find(f => f.type === 'payment' && f.active)) {
      updatedFlags.push({
        type: 'payment',
        active: true,
        created_at: new Date().toISOString(),
        auto_added: true,
      });
    } else if (!hasUnpaidInvoice) {
      // Remove payment flag if all invoices are paid
      updatedFlags = updatedFlags.filter(f => !(f.type === 'payment' && f.active));
    }

    // Note: Silence flag and regulatory flag auto-add/remove would go here
    // For now, keeping it simple - can be enhanced later

    return this.update(dealId, { flags: updatedFlags });
  },

  // Get deal with derived fields
  async getByIdWithDerived(id: string): Promise<Deal & { derived: ReturnType<typeof computeDerivedFields> }> {
    await delay(200);
    const deal = await this.getById(id);
    if (!deal) {
      throw new Error('Deal not found');
    }

    const derived = computeDerivedFields(deal);
    return {
      ...deal,
      derived,
    };
  },
};
